import { api } from "./api";

// ════════════════════════════════════════════════════════════════════════════
// Service & Category API Types
// ════════════════════════════════════════════════════════════════════════════

export interface ServiceCategory {
  id: string;
  name: string;
  displayOrder: number;
  serviceCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ServiceProvider {
  id: string;
  name: string;
  title: string | null;
  photoUrl: string | null;
  isActive: boolean;
}

export interface Service {
  id: string;
  name: string;
  description: string | null;
  durationMinutes: number;
  price: string;
  bufferMinutes: number | null;
  imageUrl: string | null;
  isActive: boolean;
  displayOrder: number;
  categoryId: string | null;
  categoryName: string | null;
  locationIds: string[] | null;
  providerCount?: number;
  providers?: ServiceProvider[];
  createdAt: string;
  updatedAt: string;
}

// ════════════════════════════════════════════════════════════════════════════
// Category API Functions
// ════════════════════════════════════════════════════════════════════════════

export async function getCategories(): Promise<ServiceCategory[]> {
  const response = await api<{ categories: ServiceCategory[] }>(
    "/api/categories"
  );
  return response.categories;
}

export async function getCategory(id: string): Promise<ServiceCategory> {
  const response = await api<{ category: ServiceCategory }>(
    `/api/categories/${id}`
  );
  return response.category;
}

export async function createCategory(data: {
  name: string;
  displayOrder?: number;
}): Promise<ServiceCategory> {
  const response = await api<{ category: ServiceCategory }>("/api/categories", {
    method: "POST",
    body: JSON.stringify(data),
  });
  return response.category;
}

export async function updateCategory(
  id: string,
  data: { name?: string; displayOrder?: number }
): Promise<ServiceCategory> {
  const response = await api<{ category: ServiceCategory }>(
    `/api/categories/${id}`,
    {
      method: "PATCH",
      body: JSON.stringify(data),
    }
  );
  return response.category;
}

export async function deleteCategory(id: string): Promise<void> {
  await api<{ success: boolean }>(`/api/categories/${id}`, {
    method: "DELETE",
  });
}

export async function reorderCategories(categoryIds: string[]): Promise<void> {
  await api<{ success: boolean }>("/api/categories/reorder", {
    method: "PUT",
    body: JSON.stringify({ categoryIds }),
  });
}

// ════════════════════════════════════════════════════════════════════════════
// Service API Functions
// ════════════════════════════════════════════════════════════════════════════

export async function getServices(options?: {
  categoryId?: string;
  active?: boolean;
}): Promise<Service[]> {
  const params = new URLSearchParams();
  if (options?.categoryId) params.append("categoryId", options.categoryId);
  if (options?.active) params.append("active", "true");

  const queryString = params.toString();
  const url = `/api/services${queryString ? `?${queryString}` : ""}`;

  const response = await api<{ services: Service[] }>(url);
  return response.services;
}

export async function getService(id: string): Promise<Service> {
  const response = await api<{ service: Service }>(`/api/services/${id}`);
  return response.service;
}

export async function createService(data: {
  name: string;
  description?: string;
  durationMinutes: number;
  price: number | string;
  bufferMinutes?: number;
  categoryId?: string;
  imageUrl?: string;
  isActive?: boolean;
  displayOrder?: number;
  locationIds?: string[];
}): Promise<Service> {
  const response = await api<{ service: Service }>("/api/services", {
    method: "POST",
    body: JSON.stringify(data),
  });
  return response.service;
}

export async function updateService(
  id: string,
  data: {
    name?: string;
    description?: string | null;
    durationMinutes?: number;
    price?: number | string;
    bufferMinutes?: number | null;
    categoryId?: string | null;
    imageUrl?: string | null;
    isActive?: boolean;
    displayOrder?: number;
    locationIds?: string[] | null;
  }
): Promise<Service> {
  const response = await api<{ service: Service }>(`/api/services/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
  return response.service;
}

export async function deleteService(id: string): Promise<void> {
  await api<{ success: boolean }>(`/api/services/${id}`, {
    method: "DELETE",
  });
}

// ════════════════════════════════════════════════════════════════════════════
// Provider Assignment API Functions
// ════════════════════════════════════════════════════════════════════════════

export async function getServiceProviders(
  serviceId: string
): Promise<ServiceProvider[]> {
  const response = await api<{ providers: ServiceProvider[] }>(
    `/api/services/${serviceId}/providers`
  );
  return response.providers;
}

export async function assignProvidersToService(
  serviceId: string,
  providerIds: string[]
): Promise<ServiceProvider[]> {
  const response = await api<{ providers: ServiceProvider[] }>(
    `/api/services/${serviceId}/providers`,
    {
      method: "POST",
      body: JSON.stringify({ providerIds }),
    }
  );
  return response.providers;
}

// ════════════════════════════════════════════════════════════════════════════
// Reorder API Functions
// ════════════════════════════════════════════════════════════════════════════

export async function reorderServices(serviceIds: string[]): Promise<void> {
  await api<{ success: boolean }>("/api/services/reorder", {
    method: "PUT",
    body: JSON.stringify({ serviceIds }),
  });
}

export async function moveService(
  serviceId: string,
  categoryId: string | null,
  displayOrder?: number
): Promise<Service> {
  const response = await api<{ service: Service }>(
    `/api/services/${serviceId}/move`,
    {
      method: "PUT",
      body: JSON.stringify({ categoryId, displayOrder }),
    }
  );
  return response.service;
}
